import React, { useState, useEffect, useCallback } from 'react';
import { getAuthData, logout, appendToGoogleSheets, hasRecordedToday, saveTodayRecord, getTodayRecord, cleanupOldRecords } from './utils/auth';
import { isHoliday, getHolidayNames } from '@hyunbinseo/holidays-kr';
import UpdateNotification from './components/UpdateNotification';
import ToastContainer from './components/ToastContainer';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [isWithinTimeWindow, setIsWithinTimeWindow] = useState(false);
  const getIsWithinTimeWindow = useCallback(() => {
    const now = new Date();
    const kstString = now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' });
    const kstDate = new Date(kstString);
    const day = kstDate.getDay(); // 0 (Sun) - 6 (Sat)
    const minutesFromMidnight = kstDate.getHours() * 60 + kstDate.getMinutes();

    const isWeekday = day >= 1 && day <= 5;
    const isNotHoliday = !isHoliday(now);
    const startMinutes = 17 * 60 + 30; // 17:30
    const endMinutes = 20 * 60; // 20:00
    const isWithinAllowedTime = minutesFromMidnight >= startMinutes && minutesFromMidnight <= endMinutes;

    return isWeekday && isNotHoliday && isWithinAllowedTime;
  }, []);

  const getUserOrgUnitName = () => {
    if (!user || !Array.isArray(user.organizations)) {
      return '';
    }

    const primaryOrg =
      user.organizations.find((org) => org.primary) || user.organizations[0];

    if (!primaryOrg || !Array.isArray(primaryOrg.orgUnits)) {
      return '';
    }

    const primaryOrgUnit =
      primaryOrg.orgUnits.find((unit) => unit.primary) || primaryOrg.orgUnits[0];

    return primaryOrgUnit?.orgUnitName || '';
  };

  useEffect(() => {
    const authData = getAuthData();
    if (authData) {
      setUser(authData.user);
    }
  }, []);

  // 사용자 로드 후 오늘 기록 여부 확인
  useEffect(() => {
    if (user && user.id) {
      // 오늘 기록 여부 확인
      const recorded = hasRecordedToday(user.id);
      setHasRecorded(recorded);

      if (recorded) {
        const record = getTodayRecord(user.id);
        setTodayRecord(record);
      }

      // 오래된 기록 정리
      cleanupOldRecords(user.id);
    }
  }, [user]);

  // Service Worker 업데이트 감지 - Custom Event 리스닝
  useEffect(() => {
    const handleServiceWorkerUpdate = (event) => {
      const { registration } = event.detail;
      console.log('Service Worker update detected!');

      if (registration && registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdateNotification(true);
      }
    };

    // Custom event listener
    window.addEventListener('serviceWorkerUpdate', handleServiceWorkerUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('serviceWorkerUpdate', handleServiceWorkerUpdate);
    };
  }, []);


  /**
   * 
   * user 객체 Example
   * {
      "id": "afd2ad0d-637a-4baa-12a5-03c55cf3f16b",
      "name": {
        "lastName": "오",
        "firstName": "장욱",
        "phoneticLastName": "",
        "phoneticFirstName": ""
      },
      "email": "jangwook.oh@beyless.com",
      "domainId": 300230381,
      "userExternalKey": null,
      "userName": {
        "lastName": "오",
        "firstName": "장욱",
        "phoneticLastName": "",
        "phoneticFirstName": ""
      },
      "i18nNames": [],
      "nickName": null,
      "privateEmail": null,
      "aliasEmails": [],
      "employmentTypeId": "e867deaa-3162-407b-9644-035567626974",
      "userTypeId": "e867deaa-3162-407b-9644-035567626974",
      "searchable": true,
      "organizations": [
        {
          "domainId": 300230381,
          "primary": true,
          "userExternalKey": null,
          "email": "jangwook.oh@beyless.com",
          "levelId": "61c231e0-7380-4538-779a-03f7fbcee930",
          "executive": false,
          "orgUnits": [
            {
              "orgUnitId": "3b886fc0-692d-4fd7-2aae-0365a5a49a01",
              "primary": true,
              "positionId": "5bce29ef-37d9-4049-8e3b-0322ce04e891",
              "isManager": true,
              "visible": true,
              "useTeamFeature": true,
              "positionName": "팀장",
              "orgUnitExternalKey": null,
              "orgUnitName": "엣지플랫폼팀",
              "orgUnitEmail": "t_007lk@beyless.com",
              "positionExternalKey": null
            }
          ],
          "organizationName": "주식회사 베이리스",
          "levelExternalKey": null,
          "levelName": "수석"
        }
      ],
      "telephone": "031-778-8171",
      "cellPhone": "+82 01024585106",
      "location": "경기기업성장센터 7층",
      "task": null,
      "messenger": null,
      "birthdayCalendarType": null,
      "birthday": null,
      "hiredDate": null,
      "locale": "ko_KR",
      "timeZone": "Asia/Seoul",
      "customFields": [],
      "relations": [],
      "employeeNumber": "59",
      "activationDate": null,
      "customProperties": null,
      "isAdministrator": false,
      "isAwaiting": false,
      "isPending": false,
      "isSuspended": false,
      "leaveOfAbsence": {
        "startTime": null,
        "endTime": null,
        "isLeaveOfAbsence": false
      },
      "isDeleted": false,
      "suspendedReason": null,
      "employmentTypeExternalKey": null,
      "employmentTypeName": "정규직",
      "userTypeExternalKey": null,
      "userTypeName": "정규직",
      "userTypeCode": null,
      "userId": "afd2ad0d-637a-4baa-12a5-03c55cf3f16b"
    }
   */
  // Helper function to safely get user name
  const getUserName = () => {
    if (!user) return '사용자';
    // Fallback to other possible field names
    const fallbackName = user.name.lastName + user.name.firstName;
    return typeof fallbackName === 'string' ? fallbackName : '사용자';
  };

  const getUserInitial = () => {
    const name = getUserName();
    if (typeof name === 'string' && name.length > 0) {
      return name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Toast 관리 함수들
  const addToast = useCallback((type, message, title = '', options = {}) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type,
      message,
      title,
      autoClose: options.autoClose !== false,
      duration: options.duration || 3000,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
    };
    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccessToast = useCallback((message, title = '성공') => {
    addToast('success', message, title);
  }, [addToast]);

  const showErrorToast = useCallback((message, title = '오류') => {
    addToast('error', message, title);
  }, [addToast]);

  const showInfoToast = useCallback((message, title = '알림') => {
    addToast('info', message, title);
  }, [addToast]);

  const showConfirmToast = useCallback((message, title = '확인', onConfirm, onCancel) => {
    addToast('confirm', message, title, {
      autoClose: false,
      onConfirm,
      onCancel
    });
  }, [addToast]);

  const handleButtonClick = async () => {
    const orgUnitName = getUserOrgUnitName();

    const withinWindow = getIsWithinTimeWindow();
    setIsWithinTimeWindow(withinWindow);

    if (!withinWindow) {
      const now = new Date();
      const holidayNames = getHolidayNames(now);
      if (holidayNames) {
        showInfoToast(`오늘은 ${holidayNames.join(', ')}입니다. 공휴일에는 이용할 수 없습니다.`, '알림');
      } else {
        showInfoToast('버튼은 평일(공휴일 제외) 17:30~20:00(한국 시간)에만 활성화됩니다.', '알림');
      }
      return;
    }

    if (!user) {
      showErrorToast('사용자 정보를 불러올 수 없습니다.');
      return;
    }

    // 이미 오늘 기록했는지 확인 (로컬 체크)
    if (hasRecorded) {
      showInfoToast('오늘은 이미 기록하셨습니다.', '알림');
      return;
    }

    setLoading(true);

    try {
      const userName = getUserName();
      const result = await appendToGoogleSheets(userName, orgUnitName);

      // 현재 시간과 날짜 생성 (SQS 비동기 처리)
      const now = new Date();
      const currentTime = now.toLocaleTimeString('ko-KR', {
        timeZone: 'Asia/Seoul',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const currentDate = now.toLocaleDateString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      // 로컬 스토리지에 기록 저장
      saveTodayRecord(user.id, {
        userName,
        orgUnitName,
        time: currentTime,
        date: currentDate
      });

      // 상태 업데이트
      setHasRecorded(true);
      setTodayRecord(getTodayRecord(user.id));

      showSuccessToast(
        `이름: ${userName}\n시간: ${currentTime}\n날짜: ${currentDate}\n상태: 처리 중`,
        '요청이 접수되었습니다!'
      );
    } catch (error) {
      console.error('Google Sheets append error:', error);

      // 서버에서 중복 기록 에러가 온 경우 (하이브리드 동기화)
      if (error.message.includes('이미 오늘 저녁식사를 기록하셨습니다')) {
        // 로컬 스토리지를 서버 상태와 동기화
        const now = new Date();
        const userName = getUserName();
        saveTodayRecord(user.id, {
          userName,
          orgUnitName,
          time: now.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul' }),
          date: now.toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })
        });

        // 상태 업데이트
        setHasRecorded(true);
        setTodayRecord(getTodayRecord(user.id));

        showInfoToast(error.message, '알림');
      } else {
        showErrorToast(error.message, '기록 실패');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    showConfirmToast(
      '로그아웃 하시겠습니까?',
      '로그아웃',
      () => logout(),
      () => {} // 취소 시 아무것도 하지 않음
    );
  };

  const handleUpdateApp = () => {
    if (waitingWorker) {
      // SKIP_WAITING 메시지를 Service Worker에 전송
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      // Service Worker가 활성화되면 페이지 새로고침
      waitingWorker.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') {
          window.location.reload();
        }
      });
    }
  };

  const handleDismissUpdate = () => {
    setShowUpdateNotification(false);
  };

  // PWA 설치 프롬프트 감지
  useEffect(() => {
    // 이미 설치되어 있는지 확인 (standalone 모드)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    
    if (isStandalone) {
      // 이미 설치되어 있으면 프롬프트 표시하지 않음
      return;
    }

    let promptEvent = null;
    let promptTimer = null;

    const handleBeforeInstallPrompt = (e) => {
      // 기본 프롬프트 방지
      e.preventDefault();
      // 이벤트 저장
      promptEvent = e;
      
      // 이전에 프롬프트를 거부했는지 확인
      const installPromptDismissed = localStorage.getItem('pwa-install-prompt-dismissed');
      if (installPromptDismissed) {
        // 7일 후에 다시 표시할 수 있도록
        const dismissedDate = new Date(installPromptDismissed);
        const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return;
        }
      }

      // 페이지 로드 후 3초 뒤에 프롬프트 표시
      promptTimer = setTimeout(() => {
        if (promptEvent) {
          showConfirmToast(
            '홈화면에 추가하시면 더 빠르게 접근할 수 있습니다.\n\n설치하시겠습니까?',
            '앱 설치',
            async () => {
              // 사용자가 동의하면 설치 프롬프트 표시
              promptEvent.prompt();
              
              // 사용자의 선택 결과 대기
              const { outcome } = await promptEvent.userChoice;
              
              if (outcome === 'accepted') {
                console.log('사용자가 PWA 설치를 수락했습니다.');
              } else {
                console.log('사용자가 PWA 설치를 거부했습니다.');
                // 거부한 경우 7일간 다시 표시하지 않음
                localStorage.setItem('pwa-install-prompt-dismissed', new Date().toISOString());
              }
              
              // 프롬프트는 한 번만 사용 가능
              promptEvent = null;
            },
            () => {
              // 취소한 경우
              localStorage.setItem('pwa-install-prompt-dismissed', new Date().toISOString());
              promptEvent = null;
            }
          );
        }
      }, 3000); // 3초 후 표시
    };

    const handleAppInstalled = () => {
      console.log('PWA가 설치되었습니다.');
      promptEvent = null;
      localStorage.removeItem('pwa-install-prompt-dismissed');
      showSuccessToast('앱이 홈화면에 추가되었습니다!', '설치 완료');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      if (promptTimer) {
        clearTimeout(promptTimer);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [showConfirmToast, showSuccessToast]);

  useEffect(() => {
    const evaluateTimeWindow = () => {
      setIsWithinTimeWindow(getIsWithinTimeWindow());
    };

    evaluateTimeWindow();
    const interval = setInterval(evaluateTimeWindow, 30 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getIsWithinTimeWindow]);

  return (
    <div className="App">
      {user && (
        <div className="user-info">
          <div className="user-profile">
            <div className="user-avatar">
              {getUserInitial()}
            </div>
            <div className="user-details">
              <p className="user-name">{getUserName()}</p>
              {/* <p className="user-email">{user.email || user.userEmail || ''}</p> */}
            </div>
          </div>
          <div className="logo-container">
            <img src="/beyless_blackLogo.png" alt="Beyless Logo" className="company-logo" />
          </div>
          <button className="logout-button" onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      )}

      <div className="main-content">
        <div className="record-status-container">
            <div className="record-status">
              {hasRecorded && todayRecord && <p className="status-icon">✓</p>}
              <p className="status-message">{(hasRecorded && todayRecord) ? "금일 저녁식사 기록 완료": "풍경 석식 명부"}</p>
              {hasRecorded && todayRecord && <p className="status-time">{todayRecord.time}</p>}
            </div>
          <div className="record-status-button-container">
            <p className="usage-note">하루에 한번만 클릭 가능하니 주의하여 사용해주세요.</p>        
            <button
              className="center-button"
              onClick={handleButtonClick}
              disabled={loading || hasRecorded || !isWithinTimeWindow}
            >
              {loading
                ? '기록 중...'
                : hasRecorded
                  ? '식사 기록 완료'
                  : isWithinTimeWindow
                    ? '기록하기'
                    : '평일 17:30~20:00에 이용해주세요.'}
            </button>
          </div>
        </div>
      </div>

      {showUpdateNotification && (
        <UpdateNotification
          onUpdate={handleUpdateApp}
          onDismiss={handleDismissUpdate}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default App;
